"use client"

import { useEffect, useState } from "react"
import dynamic from "next/dynamic"

const CookieConsentBanner = dynamic(
  () =>
    import("@/components/analytics/cookie-consent").then((m) => ({
      default: m.CookieConsentBanner,
    })),
  { ssr: false }
)

/**
 * Defer the banner mount until the browser is idle, so vanilla-cookieconsent's
 * JS doesn't compete with hydration / LCP. PSI captures its trace within the
 * first ~6s of load; under the previous immediate-mount the banner JS executed
 * during that window. Idle-mounting moves it past LCP for real users too.
 *
 * Fallback: if `requestIdleCallback` is unavailable (Safari ≤16.3, older
 * browsers) we use a 1500ms timer — comfortably past LCP on the slowest
 * connection PSI throttles to, and well within the legal "show consent
 * promptly" expectation in GDPR jurisdictions.
 */
export function LazyCookieConsent() {
  const [shouldMount, setShouldMount] = useState(false)

  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout?: number }
      ) => number
    }
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(() => setShouldMount(true), {
        timeout: 2500,
      })
      return () => {
        const cancel = (
          window as Window & { cancelIdleCallback?: (id: number) => void }
        ).cancelIdleCallback
        if (typeof cancel === "function") cancel(id)
      }
    }
    const t = setTimeout(() => setShouldMount(true), 1500)
    return () => clearTimeout(t)
  }, [])

  if (!shouldMount) return null
  return <CookieConsentBanner />
}
